/**
 * Hedita Enterprise - Maintenance Feature React Query Hooks
 * Nuovo schema: activities (tipo = 'manutenzione') con admin_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import type { MaintenanceTicket } from '@/api/queries/maintenance';

const TICKETS_KEY = 'maintenance-tickets';

// ============================================================
// TICKETS MANUTENZIONE
// ============================================================

export function useMaintenanceTickets(adminId?: string) {
  return useQuery({
    queryKey: [TICKETS_KEY, adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await supabase
        .from('activities')
        .select('*, properties(nome), units(nome)')
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as MaintenanceTicket[];
    },
    enabled: !!adminId,
  });
}

export function useMaintenanceTicket(id?: string, adminId?: string) {
  return useQuery({
    queryKey: [TICKETS_KEY, id, adminId],
    queryFn: async () => {
      if (!id || !adminId) return null;
      const { data, error } = await supabase
        .from('activities')
        .select('*, properties(*), units(*), team_members(*)')
        .eq('id', id)
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione')
        .single();
      if (error) throw error;
      return data as MaintenanceTicket | null;
    },
    enabled: !!id && !!adminId,
  });
}

export function useCreateMaintenanceTicket() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ ticket, adminId }: { ticket: Omit<MaintenanceTicket, 'id' | 'created_at' | 'updated_at'>; adminId: string }) => {
      const { data, error } = await supabase
        .from('activities')
        .insert({ ...ticket, admin_id: adminId, tipo: 'manutenzione' })
        .select()
        .single();
      if (error) throw error;
      return data as MaintenanceTicket;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.adminId] });
    },
  });
}

export function useUpdateMaintenanceTicket() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data, adminId }: { id: string; data: Partial<MaintenanceTicket>; adminId: string }) => {
      const { data: result, error } = await supabase
        .from('activities')
        .update(data)
        .eq('id', id)
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione')
        .select()
        .single();
      if (error) throw error;
      return result as MaintenanceTicket;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.adminId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.id, variables.adminId] });
    },
  });
}

export function useDeleteMaintenanceTicket() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, adminId }: { id: string; adminId: string }) => {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id)
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione');
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.adminId] });
    },
  });
}

export function useUpdateTicketStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, stato, adminId }: { id: string; stato: MaintenanceTicket['stato']; adminId: string }) => {
      const updates: any = { stato };
      if (stato === 'completata') {
        updates.data_fine = new Date().toISOString();
      }
      
      const { data, error } = await supabase
        .from('activities')
        .update(updates)
        .eq('id', id)
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione')
        .select()
        .single();
      if (error) throw error;
      return data as MaintenanceTicket;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.adminId] });
      queryClient.invalidateQueries({ queryKey: [TICKETS_KEY, variables.id, variables.adminId] });
    },
  });
}

// ============================================================
// STATS
// ============================================================

export function useMaintenanceStats(adminId?: string) {
  return useQuery({
    queryKey: [TICKETS_KEY, 'stats', adminId],
    queryFn: async () => {
      if (!adminId) return null;
      const { data, error } = await supabase
        .from('activities')
        .select('stato, priorita')
        .eq('admin_id', adminId)
        .eq('tipo', 'manutenzione');
      if (error) throw error;
      
      const stats = {
        total: data?.length || 0,
        daFare: data?.filter(t => t.stato === 'da_fare').length || 0,
        inCorso: data?.filter(t => t.stato === 'in_corso').length || 0,
        completate: data?.filter(t => t.stato === 'completata').length || 0,
        annullate: data?.filter(t => t.stato === 'annullata').length || 0,
        urgenti: data?.filter(t => t.priorita === 'urgente' && t.stato !== 'completata' && t.stato !== 'annullata').length || 0,
      };
      return stats;
    },
    enabled: !!adminId,
  });
}

// ============================================================
// RETRO-COMPATIBILITÀ
// ============================================================

/** @deprecated Usa useMaintenanceTickets */
export const useTicketsManutenzione = useMaintenanceTickets;
/** @deprecated Usa useMaintenanceTicket */
export const useTicketManutenzione = useMaintenanceTicket;
/** @deprecated Usa useCreateMaintenanceTicket */
export const useCreateTicketManutenzione = useCreateMaintenanceTicket;
/** @deprecated Usa useUpdateMaintenanceTicket */
export const useUpdateTicketManutenzione = useUpdateMaintenanceTicket;
/** @deprecated Usa useDeleteMaintenanceTicket */
export const useDeleteTicketManutenzione = useDeleteMaintenanceTicket;
/** @deprecated Usa useUpdateTicketStatus */
export const useAggiornaStatoTicket = useUpdateTicketStatus;
/** @deprecated Usa useMaintenanceStats */
export const useTicketsStats = useMaintenanceStats;
